import ConfirmModal from '@/components/ConfirmModal';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { getAccessProfiles } from '@/services/accessProfile.service';
import { getAccessByZone, removeAccessess } from '@/services/zone.service';
import { useTranslation } from 'react-i18next';
import { Icon } from '@rsuite/icons';
import React, { useEffect, useState } from 'react';
import { GrGroup } from 'react-icons/gr';
import { LuUserRound } from 'react-icons/lu';

import {
  Button,
  Checkbox,
  Drawer,
  IconButton,
  InputNumber,
  Message,
  SelectPicker,
  Stack,
  Table,
  Tabs,
  useToaster
} from 'rsuite';
import { CheckCell } from '../members/Cells';
import MoreIcon from '@rsuite/icons/legacy/More';
import { VscSave } from 'react-icons/vsc';
import TrashIcon from '@rsuite/icons/Trash';
import { getUsers } from '@/services/user.service';

const slimText = {
  fontSize: '0.9em',
  color: '#97969B',
  fontWeight: 'lighter',
  paddingBottom: 5
};

const { Column, HeaderCell, Cell } = Table;

const daysOfWeek = [
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
  { label: 'Sunday', value: 7 }
];

const AccessDrawer = ({ open, onClose, accessDrawer }) => {
  const dispatch = useAppDispatch();
  const [confirmModal, setConfirmModel] = useState<any>({ open: false });
  const toaster = useToaster();

  const zoneId = accessDrawer?.id;

  const [tab, setTab] = useState('group');

  const [checkedKeys, setCheckedKeys] = React.useState([]);

  const accessProfiles = useAppSelector((state: any) => state.accessProfile.data).map(item => ({
    ...item,
    label: item.name,
    value: item._id
  }));
  const access = useAppSelector((state: any) => state.access.data);
  const loading = useAppSelector((state: any) => state.access.loading);

  const zones = useAppSelector((state: any) => state.zone.data);

  const users = useAppSelector((state: any) => state.users.data).map(item => ({
    ...item,
    label: `${item.firstname} ${item.lastname}`,
    value: item._id
  }));

  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [deletedRow, setDeletedRow] = useState([]);

  const checked = checkedKeys.length > 0 && checkedKeys.length === filteredData?.length;
  const indeterminate = checkedKeys.length > 0 && checkedKeys.length < filteredData?.length;

  useEffect(() => {
    dispatch(getUsers());
    dispatch(getAccessByZone(zoneId));
    dispatch(getAccessProfiles());
  }, []);

  const handleChange = (value, type) => {
    switch (type) {
      case 'profile':
        setSelectedProfile(value);
        break;
      case 'user':
        setSelectedProfile(value);
        break;
      case 'dayofweek':
        setSelectedDayOfWeek(value);
        break;
      case 'priority':
        setSelectedPriority(value);
        break;
    }
  };

  const handleCheckAll = (value, checked) => {
    const keys = checked ? filteredData.map(item => item._id) : []; // Tous les IDs si coché, sinon vide
    setCheckedKeys(keys);
  };

  const handleCheck = (value, checked) => {
    const keys = checked ? [...checkedKeys, value] : checkedKeys.filter(item => item !== value);
    setCheckedKeys(keys);
  };

  useEffect(() => {
    const resultFiltered = access.filter(item => {
      const hasTabField = tab === 'user' ? item.user : item.group;

      const matchProfile =
        !selectedProfile ||
        (tab === 'user' ? item.user?._id === selectedProfile : item.group?._id === selectedProfile);
      const matchDayOfWeek = !selectedDayOfWeek || item.dayOfWeek === selectedDayOfWeek;
      const matchPriority = !selectedPriority || Number(item.priority) == Number(selectedPriority);

      return hasTabField && matchProfile && matchDayOfWeek && matchPriority;
    });

    setFilteredData(resultFiltered);
  }, [access, selectedProfile, selectedDayOfWeek, selectedPriority, tab]);

  const handleDeleteSelected = async () => {
    try {
      dispatch(removeAccessess(checkedKeys))
        .then(() => {
          setConfirmModel({ open: false });
          toaster.push(
            <Message type="success" duration={5000} showIcon>
              {t('Deleted successfully')}
            </Message>,
            { placement: 'topCenter' }
          );
          setCheckedKeys([]);
        })
        .catch(e => {
          toaster.push(
            <Message type="error" duration={5000} showIcon>
              {t(e.message)}
            </Message>,
            { placement: 'topCenter' }
          );
        });
    } catch (error) {
      console.error('Error while deleting accesses', error);
    }
  };

  const handleDeleteRow = id => {
    // const updatedFilteredData = filteredData.filter(item => item._id !== id);
    // setFilteredData(updatedFilteredData);
    // setDeletedRow(prev => [...prev, id]);

    try {
      dispatch(removeAccessess([id]))
        .then(() => {
          setConfirmModel({ open: false });
          toaster.push(
            <Message type="success" duration={5000} showIcon>
              {t('Deleted successfully')}
            </Message>,
            { placement: 'topCenter' }
          );
        })
        .catch(e => {
          toaster.push(
            <Message type="error" duration={5000} showIcon>
              {t(e.message)}
            </Message>,
            { placement: 'topCenter' }
          );
        });
    } catch (error) {
      console.error('Error while deleting accesses', error);
    }
  };

  // const handleDeleteSelected = () => {
  //   const updatedFilteredData = filteredData.filter(item => !checkedKeys.includes(item._id));
  //   setFilteredData(updatedFilteredData);
  //   setDeletedRow(prev => [...prev, ...checkedKeys]);
  // };
  const { t } = useTranslation();

  return (
    <Drawer backdrop="static" size="lg" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{`Access for  ${zones.find(zone => zone._id == zoneId)?.name}`}</Drawer.Title>
      </Drawer.Header>

      <Drawer.Body>
        <Stack justifyContent="center">
          <Tabs
            defaultActiveKey="group"
            appearance="pills"
            onSelect={val => {
              setTab(val);
              setDeletedRow([]);
              setCheckedKeys([]);
            }}
          >
            <Tabs.Tab eventKey="group" title={t('Profile Accesses')} icon={<GrGroup />}></Tabs.Tab>
            <Tabs.Tab eventKey="user" title={t('Users Accesses')} icon={<LuUserRound />}></Tabs.Tab>
          </Tabs>
        </Stack>

        {tab == 'group' ? (
          <div style={{ marginTop: 50 }}>
            <Stack justifyContent="space-between">
              <Stack spacing={5}>
                <div style={{ textAlign: 'left' }}>
                  <div style={slimText}>Profile</div>

                  <SelectPicker
                    data={accessProfiles}
                    onChange={value => handleChange(value, 'profile')}
                    value={selectedProfile}
                    style={{ width: 150 }}
                    placement="auto"
                  />
                </div>

                <div style={{ textAlign: 'left' }}>
                  <div style={slimText}>{t('Day of week')}</div>
                  <SelectPicker
                    data={daysOfWeek}
                    onChange={value => handleChange(value, 'dayofweek')}
                    value={selectedDayOfWeek}
                    style={{ width: 150 }}
                    placement="auto"
                  />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={slimText}>{t('Priority')}</div>
                  <InputNumber
                    style={{ width: 70 }}
                    min={0}
                    max={5}
                    onChange={value => setSelectedPriority(value)}
                    value={selectedPriority}
                  />
                </div>
              </Stack>

              {checkedKeys.length > 0 && (
                <IconButton
                  icon={<TrashIcon />}
                  // onClick={() => {
                  //   handleDeleteSelected();
                  // }}

                  onClick={() => {
                    setConfirmModel({
                      open: true,
                      title: 'Are you sure you want to delete these items?',
                      subtitle: 'You cannot undo this operation',
                      onConfirm: () => {
                        handleDeleteSelected();
                      }
                    });
                  }}
                  appearance="primary"
                  color="red"
                  id="delete-selected"
                >
                  {t('Delete selected')}
                </IconButton>
              )}
            </Stack>
            <Table
              height={300}
              data={filteredData}
              id="table-group"
              loading={loading}
              style={{ marginTop: 50 }}
            >
              <Column width={50} align="center">
                <HeaderCell style={{ padding: 0 }}>
                  <div style={{ lineHeight: '40px' }}>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </HeaderCell>
                <CheckCell dataKey="_id" checkedKeys={checkedKeys} onChange={handleCheck} />
              </Column>
              <Column width={160} flexGrow={1}>
                <HeaderCell>Profile</HeaderCell>
                <Cell dataKey="name">{rowData => rowData.group?.name}</Cell>
              </Column>

              <Column width={160}>
                <HeaderCell>{t('Day of week')}</HeaderCell>
                <Cell dataKey="name">
                  {rowData => daysOfWeek.find(day => day.value == rowData.dayOfWeek).label}
                </Cell>
              </Column>

              <Column width={230}>
                <HeaderCell>{t('Priority')}</HeaderCell>
                <Cell style={{ padding: '10px 0' }}>{rowData => rowData.priority}</Cell>
              </Column>

              {checkedKeys?.length == 0 && (
                <Column width={120} flexGrow={0.5} fixed="right">
                  <HeaderCell>
                    <MoreIcon />
                  </HeaderCell>
                  <Cell>
                    {rowData => (
                      <IconButton
                        appearance="subtle"
                        size="sm"
                        icon={<TrashIcon color="red" />}
                        // onClick={() => {
                        //   console.log(rowData._id);
                        //   handleDeleteRow(rowData._id);
                        // }}

                        onClick={() => {
                          setConfirmModel({
                            open: true,
                            title: 'Are you sure you want to delete this item?',
                            subtitle: 'You cannot undo this operation',
                            onConfirm: () => {
                              handleDeleteRow(rowData._id);
                            }
                          });
                        }}
                        id={`delete-${rowData._id}`}
                      />
                    )}
                  </Cell>
                </Column>
              )}
            </Table>
          </div>
        ) : (
          <div style={{ marginTop: 50 }}>
            <Stack justifyContent="space-between">
              <Stack spacing={5}>
                <div style={{ textAlign: 'left' }}>
                  <div style={slimText}>User</div>

                  <SelectPicker
                    data={users}
                    onChange={value => handleChange(value, 'user')}
                    value={selectedProfile}
                    style={{ width: 150 }}
                    placement="auto"
                  />
                </div>

                <div style={{ textAlign: 'left' }}>
                  <div style={slimText}>{t('Day of week')}</div>
                  <SelectPicker
                    data={daysOfWeek}
                    onChange={value => handleChange(value, 'dayofweek')}
                    value={selectedDayOfWeek}
                    style={{ width: 150 }}
                    placement="auto"
                  />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={slimText}>{t('Priority')}</div>
                  <InputNumber
                    style={{ width: 70 }}
                    min={0}
                    max={5}
                    onChange={value => setSelectedPriority(value)}
                    value={selectedPriority}
                  />
                </div>
              </Stack>

              {checkedKeys.length > 0 && (
                <IconButton
                  icon={<TrashIcon />}
                  // onClick={() => {
                  //   handleDeleteSelected();
                  // }}
                  onClick={() => {
                    setConfirmModel({
                      open: true,
                      title: 'Are you sure you want to delete these items?',
                      subtitle: 'You cannot undo this operation',
                      onConfirm: () => {
                        handleDeleteSelected();
                      }
                    });
                  }}
                  appearance="primary"
                  color="red"
                  id="delete-selected"
                >
                  {t('Delete selected')}
                </IconButton>
              )}
            </Stack>
            <Table
              height={300}
              data={filteredData}
              id="table-group"
              loading={loading}
              style={{ marginTop: 50 }}
            >
              <Column width={50} align="center">
                <HeaderCell style={{ padding: 0 }}>
                  <div style={{ lineHeight: '40px' }}>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </HeaderCell>
                <CheckCell dataKey="_id" checkedKeys={checkedKeys} onChange={handleCheck} />
              </Column>
              <Column width={160} flexGrow={1}>
                <HeaderCell>{t('User')}</HeaderCell>
                <Cell dataKey="user">
                  {rowData => rowData.user?.firstname + ' ' + rowData?.user?.lastname}
                </Cell>
              </Column>

              <Column width={160}>
                <HeaderCell>{t('Day of week')}</HeaderCell>
                <Cell dataKey="dayOfWeek">
                  {rowData => daysOfWeek.find(day => day.value == rowData.dayOfWeek).label}
                </Cell>
              </Column>

              <Column width={230}>
                <HeaderCell>{t('Priority')}</HeaderCell>
                <Cell style={{ padding: '10px 0' }}>{rowData => rowData.priority}</Cell>
              </Column>

              {checkedKeys?.length == 0 && (
                <Column width={120} flexGrow={0.5} fixed="right">
                  <HeaderCell>
                    <MoreIcon />
                  </HeaderCell>
                  <Cell>
                    {rowData => (
                      <IconButton
                        appearance="subtle"
                        size="sm"
                        icon={<TrashIcon color="red" />}
                        // onClick={() => {
                        //   console.log(rowData._id);
                        //   handleDeleteRow(rowData._id);
                        // }}
                        onClick={() => {
                          setConfirmModel({
                            open: true,
                            title: 'Are you sure you want to delete this item?',
                            subtitle: 'You cannot undo this operation',
                            onConfirm: () => {
                              handleDeleteRow(rowData._id);
                            }
                          });
                        }}
                        id={`delete-${rowData._id}`}
                      />
                    )}
                  </Cell>
                </Column>
              )}
            </Table>
          </div>
        )}
      </Drawer.Body>

      {confirmModal.open && (
        <ConfirmModal confirmDialog={confirmModal} setConfirmDialog={setConfirmModel} />
      )}
    </Drawer>
  );
};

export default AccessDrawer;
